import { useCallback, useEffect, useMemo } from "react";

import { useRecoilState } from "recoil";

import { useBlizzardEvents } from "../../analytics/hooks";
import { usePagination } from "../../core/pagination/hooks";
import { LensSpaceLensFilterStatuses } from "../../core/pagination/models";
import { type MyLensesLensStatus } from "../../gql-generated/types";
import { PaginationAtomsKeys } from "../../shared/state";
import { filterByStatusAtom, filterIsEmptyAtom } from "../state";

export const useLensFilterByStatus = (): {
    selectedAll: boolean;
    filterIsEmpty: boolean;
    filterByStatus: MyLensesLensStatus[];
    toggleAllSelected: () => void;
    updateFilter: (nextStatus: MyLensesLensStatus) => void;
    onFilterChange: () => void;
} => {
    const { updateFilterBy } = usePagination();
    const [filterIsEmpty, setFilterIsEmpty] = useRecoilState(filterIsEmptyAtom(PaginationAtomsKeys.LensesList));
    const [filterByStatus, setFilterByStatus] = useRecoilState(filterByStatusAtom(PaginationAtomsKeys.LensesList));
    const { sendBlizzardEvent } = useBlizzardEvents();
    const selectedAll = useMemo(() => filterByStatus.length === 0 && !filterIsEmpty, [filterIsEmpty, filterByStatus]);

    const listOfAllStatuses = Object.values(LensSpaceLensFilterStatuses);

    const toggleAllSelected = useCallback(() => {
        if (filterIsEmpty) {
            setFilterIsEmpty(false);
        } else if (selectedAll) {
            setFilterIsEmpty(true);
        } else {
            setFilterByStatus([]);
        }
    }, [setFilterByStatus, selectedAll, setFilterIsEmpty, filterIsEmpty]);

    const updateFilter = useCallback(
        (nextStatus: MyLensesLensStatus) => {
            let nextFilter = [...filterByStatus, nextStatus];
            if (selectedAll) {
                nextFilter = listOfAllStatuses.filter((status) => status !== nextStatus);
            }
            if (filterByStatus.includes(nextStatus)) {
                nextFilter = filterByStatus.filter((status) => status !== nextStatus);
            }

            setFilterIsEmpty(nextFilter.length === 0);

            if (nextFilter.length === listOfAllStatuses.length) {
                nextFilter = [];
            }

            setFilterByStatus(nextFilter);
        },
        [filterByStatus, setFilterByStatus, setFilterIsEmpty, listOfAllStatuses, selectedAll],
    );

    const onFilterChange = useCallback(() => {
        sendBlizzardEvent({
            event: "MYLENSES_MANAGEMENT_PAGE_ACTION",
            pageType: "LENS_TABLE",
            action: "FILTER_LENSES",
        });

        if (selectedAll || filterByStatus.length > 0) {
            updateFilterBy(filterByStatus);
        }
    }, [filterByStatus, selectedAll, updateFilterBy, sendBlizzardEvent]);

    useEffect(() => {
        return () => {
            setFilterIsEmpty(false);
            setFilterByStatus([]);
        };
    }, [setFilterIsEmpty, setFilterByStatus]);

    return {
        selectedAll,
        filterIsEmpty,
        filterByStatus,
        toggleAllSelected,
        updateFilter,
        onFilterChange,
    };
};
